'use client'

import { useCallback, useEffect, useRef } from 'react'

type RippleProps = {
  color?: string
  duration?: number
}

export default function useRipple<T extends HTMLElement>({
  color = '#ffffff',
  duration = 700,
}: RippleProps) {
  const ref = useRef<T>(null)

  const clickHandler = useCallback(
    ({
      pageX,
      pageY,
      currentTarget,
    }: {
      pageX: number
      pageY: number
      currentTarget: EventTarget | null
    }) => {
      if (!(currentTarget instanceof HTMLElement)) return

      const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = currentTarget

      const x = ((pageX - offsetLeft) * 100) / offsetWidth
      const y = ((pageY - offsetTop) * 100) / offsetHeight

      const ripple = document.createElement('span')

      ripple.classList.add('ripple-effect')
      ripple.style.background = color
      ripple.style.animation = `ripple ${duration}ms linear infinite`

      currentTarget.appendChild(ripple)
      ripple.style.left = x + '%'
      ripple.style.top = y + '%'

      setTimeout(() => {
        ripple.remove()
      }, duration)
    },
    [color, duration],
  )

  useEffect(() => {
    const currentElement = ref.current
    currentElement?.addEventListener('mousedown', clickHandler)
    return () => currentElement?.removeEventListener('mousedown', clickHandler)
  }, [ref, clickHandler])

  return ref
}
